import { useState, useEffect, useCallback, useMemo } from 'react';
import type { Notification } from '../types';
import { notificationRepository } from '../data/notificationRepository';

/**
 * useNotifications フックの戻り値
 */
interface UseNotificationsReturn {
  /** 全データ */
  data: Notification[];
  /** 未読件数 */
  unreadCount: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * お知らせ一覧を取得するフック
 */
export const useNotifications = (accountId?: string, token?: string): UseNotificationsReturn => {
  const [data, setData] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // accountIdが指定されていない場合はデフォルト値を使用（モック用）
      const result = await notificationRepository.getAll(accountId || '', token);
      if (result.success) {
        setData(result.data);
      } else {
        throw new Error(result.error.message);
      }
    } catch (e) {
      setError(e as Error);
    } finally {
      setIsLoading(false);
    }
  }, [accountId, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const unreadCount = useMemo(
    () => data.filter((n) => n.status === 'unread').length,
    [data]
  );

  return {
    data,
    unreadCount,
    isLoading,
    error,
    refetch: fetchData,
  };
};
